import genericAvatar from "assets/images/generic_avatar.jpg";
import { ErrorModal, PhotoUploader } from "components";
import { getDownloadURL } from "firebase/storage";
import { CuratedOrganization, emptyMayTerm } from "models";
import React, { useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import Img from "react-cool-img";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { addMayTermStory, updateMayTermStory, uploadProfilePhoto } from "services";

interface AddEditMayTermModalProps {
  /** Whether the modal should be shown. */
  show: boolean;

  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** Function to be called when canceling or closing the modal. */
  // onCancel: () => void;
  /** The alum to edit, or undefined if creating a new alum. */
  currentMayTerm: CuratedOrganization | undefined;
}

/** Modal for adding/editing curated alumni. */
export function AddEditMayTermModal({
  show,
  setShow,
  currentMayTerm,
}: AddEditMayTermModalProps): JSX.Element {
  const [editedMayTerm, setEditedMayTerm] = useState<CuratedOrganization>(
    currentMayTerm ? { ...currentMayTerm } : { ...emptyMayTerm },
  );
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | undefined>(undefined);
  const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
  const isNew = currentMayTerm === undefined;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [address, setAddress] = useState(setMayTermLocation());
  editedMayTerm.location = address;
  const [coordinates, setCoordinates] = React.useState({
    lat: 32.779167,
    lng: -96.808891,
  });
  editedMayTerm.longitude = coordinates.lng;
  editedMayTerm.latitude = coordinates.lat;
  editedMayTerm.type = "may-term";

  const handleSelect = async (mapvalue: string) => {
    const results = await geocodeByAddress(mapvalue);
    const latLng = await getLatLng(results[0]);
    setAddress(mapvalue);
    setCoordinates(latLng);
  };

  const handleClose = () => {
    setShow(false);
  };

  /**
   * This method will handle the submit function
   * First checking if the admin has submitted all the required fields
   * Then upload the photo to firebase storage and upload photo url to firestore with the profile
   * Finally upload the profile to firestore
   */
  async function submitMayTerm(event: {
    currentTarget: any;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault(); // prevents the page from reloading immediately on submit

      if (uploadedPhoto !== undefined) {
        const uploadTask = uploadProfilePhoto(uploadedPhoto, editedMayTerm);
        await uploadTask;
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        editedMayTerm.photoUrl = url;
      }

      const submitFn: () => Promise<unknown> = isNew
        ? () => addMayTermStory(editedMayTerm)
        : () => updateMayTermStory(editedMayTerm.id, editedMayTerm);

      submitFn()
        .catch(err => setError(err))
        .finally(() => {
          // history.push("map");
          // history.push("map-view-admin");
          window.location.reload(); // this is not the best but my window was reloading so hard with historuy.push
        });
      setIsSubmitting(true);
    }
    setValidated(true);
  }

  /**
   * A function that will return the address/location of the current MayTerm, or
   * an empty string if null
   * @returns The location of the current (unedited) MayTerm, or an empty string if it is a new MayTerm
   */
  function setMayTermLocation() {
    if (currentMayTerm) {
      return currentMayTerm.location;
    }
    return "";
  }

  return (
    <>
      <Modal show={show} size="lg" className="add-edit-alumni-modal">
        <Form noValidate validated={validated} onSubmit={submitMayTerm}>
          <Modal.Header>
            <Modal.Title>
              {isNew ? "Add New Travel Opportunity" : "Edit Travel Opportunity"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <strong>Basic Info</strong>
            </Form.Group>
            <Form.Row>
              <Col>
                <MayTermFormGroup
                  type="text"
                  attribute="date"
                  label="Date of Term"
                  placeholder={`May ${new Date().getFullYear().toString()}`}
                  onChange={e =>
                    setEditedMayTerm({ ...editedMayTerm, date: e.target.value.trim() })
                  }
                  defaultValue={editedMayTerm.date || ""}
                  required
                />
              </Col>
              <Form.Control.Feedback type="invalid">
                Please provide a valid Date.
              </Form.Control.Feedback>

              <Col>
                <MayTermFormGroup
                  attribute="organization"
                  label="Partner Organization"
                  placeholder="e.g. Liverpool Hope University"
                  onChange={e =>
                    setEditedMayTerm({ ...editedMayTerm, organization: e.target.value.trim() })
                  }
                  defaultValue={editedMayTerm.organization || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid Partner Organization Name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col>
                <Form.Label>
                  Address
                  <span className="text-danger"> *</span>
                </Form.Label>

                <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                      <Form.Control {...getInputProps({ placeholder: "Type address" })} required />
                      <div>
                        {loading ? <div>...loading</div> : null}

                        {suggestions.map(suggestion => {
                          const style = {
                            backgroundColor: suggestion.active ? "#41b6e6" : "#fff",
                          };

                          return (
                            <div {...getSuggestionItemProps(suggestion, { style })}>
                              {suggestion.description}
                            </div>
                          );
                        })}
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid Location.
                      </Form.Control.Feedback>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Col>

              <Col>
                <MayTermFormGroup
                  attribute="email"
                  label="Contact Person Email"
                  placeholder="e.g. contact.person@hope.edu"
                  onChange={e =>
                    setEditedMayTerm({ ...editedMayTerm, email: e.target.value.trim() })
                  }
                  defaultValue={editedMayTerm.email || ""}
                />
              </Col>
            </Form.Row>

            {/* Sue wasn't a fan of the catchphrase.
            <Form.Row>
              <Col>
                <MayTermFormGroup
                  attribute="catchphrase"
                  label="Catchphrase"
                  placeholder="e.g. In God We Trust"
                  onChange={e =>
                    setEditedMayTerm({ ...editedMayTerm, catchphrase: e.target.value.trim() })
                  }
                  defaultValue={editedMayTerm.catchphrase || ""}
                />
              </Col>
            </Form.Row> */}

            <Form.Group>
              <Form.Label>Advertisement Image</Form.Label>
              {!!editedMayTerm.photoUrl && !isReplacingPhoto ? (
                <Form.Row>
                  <Col xs={2}>
                    <Img
                      src={editedMayTerm.photoUrl || ""}
                      placeholder={genericAvatar}
                      alt={`${editedMayTerm.description}`}
                      width="100%"
                      loading="lazy"
                      className="img-circle"
                    />
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Button variant="outline-secondary" onClick={() => setIsReplacingPhoto(true)}>
                      Replace
                    </Button>
                  </Col>
                </Form.Row>
              ) : (
                <PhotoUploader onDrop={(photos: File[]) => setUploadedPhoto(photos[0])} />
              )}
            </Form.Group>

            <MayTermFormGroup
              as="textarea"
              rows={6}
              attribute="description"
              label="Description"
              placeholder="Enter some interesting information about this travel opportunity"
              onChange={e =>
                setEditedMayTerm({ ...editedMayTerm, description: e.target.value.trim() })
              }
              defaultValue={editedMayTerm.description}
              required
            />

            <Form.Group>
              <strong>Other Info</strong>
            </Form.Group>

            <MayTermFormGroup
              as="textarea"
              rows={3}
              attribute="quotes"
              label="What Other Students Are Saying!!"
              placeholder="Enter any quotes separated by pipes (quote1 | quote2 | ...)"
              onChange={e =>
                setEditedMayTerm({
                  ...editedMayTerm,
                  quotes:
                    e.target.value === ""
                      ? []
                      : e.target.value.split("|").map(quote => quote.trim()),
                })
              }
              defaultValue={editedMayTerm.quotes?.join(" | ") || ""}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="outline-primary"
              type="submit"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ErrorModal error={error} />
    </>
  );
}

interface MayTermFormGroupProps {
  attribute: keyof CuratedOrganization;
  type?: "text" | "number";
  as?: "textarea";
  label: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  defaultValue: string;
  required?: boolean;
  rows?: number;
}

function MayTermFormGroup({
  attribute,
  label,
  placeholder,
  onChange,
  defaultValue,
  type,
  as,
  required,
  rows,
}: MayTermFormGroupProps) {
  return (
    <Form.Group controlId={attribute}>
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Control
        placeholder={placeholder}
        onChange={onChange}
        type={type || "text"}
        defaultValue={defaultValue}
        as={as}
        required={required}
        rows={rows}
      />
    </Form.Group>
  );
}
